
import { useState, useEffect, useRef } from 'react';
import { useGame } from '../contexts/GameContext';
import { toast } from "@/components/ui/sonner";
import { handleYearCompletion, createSimulationFailsafe } from './simulation/timeProgression';
import { SimulationTimeState } from './simulation/types';

export const useTimeProgression = (
  setDecisionsThrottled: React.Dispatch<React.SetStateAction<boolean>>,
  setDecisionTimerFired: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
) => {
  const { 
    yearsPassed, 
    settings, 
    advanceYear,
    currentSpecialEvent,
    currentChallenge
  } = useGame();
  
  // Default year duration to 3 minutes (180 seconds) if not set in settings
  const yearDuration = settings?.yearDuration || 180;
  
  const [timeState, setTimeState] = useState<SimulationTimeState>({
    yearProgress: 0,
    timeLeft: yearDuration,
    yearDuration,
    isSimulating: false
  });
  
  // Store timer ID for cleanup
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to handle year duration changes in settings
  useEffect(() => {
    setTimeState(prev => ({
      ...prev,
      yearDuration,
      timeLeft: prev.isSimulating ? prev.timeLeft : yearDuration
    }));
  }, [yearDuration]);

  // Don't allow time passage if there's a pending special event or challenge
  const canStartSimulation = !currentSpecialEvent && !currentChallenge;

  const startTimePassage = (onStartCallback?: () => void) => {
    if (!canStartSimulation) {
      toast(`You have a pending decision to make!`, {
        description: "Handle the current situation before advancing time.",
        duration: 3000,
      });
      return;
    }
    
    setTimeState(prev => ({
      ...prev,
      isSimulating: true,
      yearProgress: 0,
      timeLeft: yearDuration
    }));
    
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // If there's a callback, execute it
    if (onStartCallback) {
      onStartCallback();
    }
    
    // Start countdown timer that updates every second
    timerRef.current = setInterval(() => {
      setTimeState(prev => {
        if (prev.timeLeft <= 0) {
          clearInterval(timerRef.current!);
          handleYearCompletion(
            yearsPassed,
            advanceYear,
            setDecisionsThrottled,
            setDecisionTimerFired,
            () => setTimeState(prev => ({ ...prev, isSimulating: false })),
            yearDuration,
            newTimeLeft => setTimeState(prev => ({ ...prev, timeLeft: newTimeLeft }))
          );
          return {
            ...prev,
            timeLeft: yearDuration,
            isSimulating: false
          };
        }
        
        // Update progress based on time left
        const newProgress = ((yearDuration - prev.timeLeft + 1) / yearDuration) * 100;
        
        return {
          ...prev,
          yearProgress: newProgress,
          timeLeft: prev.timeLeft - 1
        };
      });
    }, 1000);
    
    // Failsafe: If for some reason the year gets stuck, force complete
    createSimulationFailsafe(
      true,
      timerRef,
      advanceYear,
      toast,
      yearsPassed,
      () => setTimeState(prev => ({ ...prev, isSimulating: false })),
      () => setTimeState(prev => ({ ...prev, yearProgress: 0 })),
      setDecisionsThrottled,
      (newTimeLeft) => setTimeState(prev => ({ ...prev, timeLeft: newTimeLeft })),
      yearDuration
    );
  };

  return {
    timeState,
    timerRef,
    startTimePassage,
    canStartSimulation
  };
};
